import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  FileText,
  Loader2,
  RefreshCcw,
  Search,
  X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import AgentPageShell from '../../components/agent/AgentPageShell';
import { cn } from '../../lib/utils';
import {
  fetchKnowledgeSources,
  saveKnowledgeSources,
  syncKnowledgeSources,
} from '../../lib/agentApi';
import { formatWhen } from './AgentDashboardPage';

const FILTERS = ['All', 'Connected', 'Excluded'];

const KnowledgeSourcesPage = () => (
  <AgentPageShell
    title="Knowledge Source Mapping"
    description="Choose which spaces and documents this agent is allowed to read"
    icon={Database}
    breadcrumb={['Knowledge Sources']}
  >
    {({ agent, orgId }) => <SourcesBody key={agent.id} agent={agent} orgId={orgId} />}
  </AgentPageShell>
);

const SourcesBody = ({ agent, orgId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');
  const [expanded, setExpanded] = useState({});

  // Draft selection — nothing is persisted until Save Changes.
  const [selectedSpaces, setSelectedSpaces] = useState([]);
  const [excludedDocs, setExcludedDocs] = useState([]);
  const [settings, setSettings] = useState({});
  const [keywordDraft, setKeywordDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchKnowledgeSources(orgId, agent.id);
      setData(result);
      setSelectedSpaces(result.spaces.filter((s) => s.included).map((s) => s.id));
      setExcludedDocs(
        result.spaces.flatMap((s) => s.documents.filter((d) => !d.included).map((d) => d.id))
      );
      setSettings(result.settings || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load knowledge sources');
    } finally {
      setLoading(false);
    }
  }, [orgId, agent.id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSpace = (spaceId, next) => {
    setSelectedSpaces((prev) =>
      next ? [...new Set([...prev, spaceId])] : prev.filter((s) => s !== spaceId)
    );
  };

  const toggleDoc = (docId) => {
    setExcludedDocs((prev) =>
      prev.includes(docId) ? prev.filter((d) => d !== docId) : [...prev, docId]
    );
  };

  const toggleAllDocs = (space) => {
    const ids = space.documents.map((d) => d.id);
    const allExcluded = ids.every((id) => excludedDocs.includes(id));
    setExcludedDocs((prev) =>
      allExcluded ? prev.filter((d) => !ids.includes(d)) : [...new Set([...prev, ...ids])]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveKnowledgeSources(orgId, agent.id, {
        space_ids: selectedSpaces,
        excluded_doc_ids: excludedDocs,
        settings,
      });
      toast.success('Knowledge sources saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async (spaceId) => {
    setSyncing(true);
    try {
      const result = await syncKnowledgeSources(orgId, agent.id, spaceId);
      toast.success(
        spaceId ? 'Space re-indexed' : `Re-indexed ${result.results?.length || 0} space(s)`
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const visibleSpaces = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.spaces.filter((space) => {
      if (filter === 'Connected' && !selectedSpaces.includes(space.id)) return false;
      if (filter === 'Excluded' && selectedSpaces.includes(space.id)) return false;
      if (!q) return true;
      return (
        space.name.toLowerCase().includes(q) ||
        space.documents.some((d) => (d.name || '').toLowerCase().includes(q))
      );
    });
  }, [data, query, filter, selectedSpaces]);

  const dirty =
    data &&
    (JSON.stringify([...selectedSpaces].sort()) !==
      JSON.stringify(data.spaces.filter((s) => s.included).map((s) => s.id).sort()) ||
      JSON.stringify([...excludedDocs].sort()) !==
        JSON.stringify(
          data.spaces
            .flatMap((s) => s.documents.filter((d) => !d.included).map((d) => d.id))
            .sort()
        ));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading knowledge sources…
      </div>
    );
  }
  if (!data) return null;

  const totalDocs = data.spaces
    .filter((s) => selectedSpaces.includes(s.id))
    .reduce((sum, s) => sum + s.doc_count, 0);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          Last synced: {formatWhen(data.last_synced_at) || 'never'}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleSync()} disabled={syncing}>
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCcw className="w-4 h-4 mr-2" />
            )}
            Sync All Sources
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !dirty}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Connected Spaces" value={selectedSpaces.length} dot="bg-emerald-500" />
        <StatCard label="Total Documents" value={totalDocs} />
        <StatCard label="Excluded Documents" value={excludedDocs.length} dot="bg-amber-500" />
      </div>

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search spaces or documents..."
            className="pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                filter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Spaces */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">DocTree Spaces</h2>
            <Badge variant="secondary" className="rounded-full px-2 py-0">
              {visibleSpaces.length}
            </Badge>
          </div>

          {visibleSpaces.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center text-sm text-muted-foreground">
                No spaces match this filter.
              </CardContent>
            </Card>
          ) : (
            visibleSpaces.map((space) => {
              const included = selectedSpaces.includes(space.id);
              const isOpen = expanded[space.id];
              const excludedHere = space.documents.filter((d) =>
                excludedDocs.includes(d.id)
              ).length;

              return (
                <div
                  key={space.id}
                  className={cn(
                    'border rounded-xl overflow-hidden transition-all',
                    included ? 'bg-card border-border' : 'bg-muted/30 border-dashed opacity-80'
                  )}
                >
                  <div className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0">
                      {space.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">{space.name}</h3>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {space.doc_count} docs
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <SyncBadge status={space.sync_status} />
                        {space.indexed_chunks > 0 && (
                          <span className="text-[11px] text-muted-foreground">
                            {space.indexed_chunks} chunks
                          </span>
                        )}
                        {excludedHere > 0 && (
                          <span className="text-[11px] text-amber-600 dark:text-amber-400">
                            {excludedHere} excluded
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {included && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => handleSync(space.id)}
                          disabled={syncing}
                          title="Re-index this space"
                        >
                          <RefreshCcw className="w-4 h-4" />
                        </Button>
                      )}
                      <Switch
                        checked={included}
                        onCheckedChange={(next) => toggleSpace(space.id, next)}
                        aria-label={`Include ${space.name}`}
                      />
                      <button
                        onClick={() => setExpanded((p) => ({ ...p, [space.id]: !p[space.id] }))}
                        className="p-1 hover:bg-muted rounded-md transition-colors"
                        aria-label="Toggle documents"
                      >
                        {isOpen ? (
                          <ChevronDown className="w-5 h-5" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t bg-muted/20">
                      {space.documents.length === 0 ? (
                        <p className="px-4 py-6 text-xs text-center text-muted-foreground">
                          This space has no documents yet.
                        </p>
                      ) : (
                        <>
                          <div className="px-4 py-2 flex items-center justify-between border-b border-border/50">
                            <button
                              className="text-xs font-medium text-muted-foreground hover:text-foreground"
                              onClick={() => toggleAllDocs(space)}
                            >
                              {space.documents.every((d) => excludedDocs.includes(d.id))
                                ? 'Include all'
                                : 'Exclude all'}
                            </button>
                            <span className="text-xs text-muted-foreground">
                              {space.documents.length - excludedHere} of {space.documents.length}{' '}
                              included
                            </span>
                          </div>
                          <div className="divide-y divide-border/50 max-h-72 overflow-y-auto agent-scrollbar">
                            {space.documents.map((doc) => (
                              <div
                                key={doc.id}
                                className="px-4 py-2.5 flex items-center justify-between hover:bg-muted/30"
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <Checkbox
                                    checked={!excludedDocs.includes(doc.id)}
                                    onCheckedChange={() => toggleDoc(doc.id)}
                                  />
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                      <span className="text-sm font-medium truncate">
                                        {doc.name}
                                      </span>
                                    </div>
                                    <span className="text-[11px] text-muted-foreground">
                                      Updated {formatWhen(doc.updated_at) || '—'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Settings */}
        <Card className="h-fit lg:sticky lg:top-6">
          <CardContent className="p-6 space-y-6">
            <div>
              <h3 className="font-semibold">Source Settings</h3>
              <p className="text-sm text-muted-foreground">
                How this agent processes the selected sources
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Knowledge freshness</Label>
                <span className="text-sm font-semibold text-primary">
                  {settings.freshness_hours >= 168
                    ? '7d'
                    : `${settings.freshness_hours ?? 24}h`}
                </span>
              </div>
              <Slider
                value={[settings.freshness_hours ?? 24]}
                onValueChange={([v]) => setSettings((s) => ({ ...s, freshness_hours: v }))}
                min={1}
                max={168}
                step={1}
              />
              <div className="flex justify-between text-[10px] text-muted-foreground uppercase font-medium">
                <span>Real-time</span>
                <span>Balanced</span>
                <span>Efficient</span>
              </div>
            </div>

            <div className="space-y-4">
              <SettingSwitch
                label="Auto-sync on publish"
                checked={settings.auto_sync_on_publish ?? true}
                onChange={(v) => setSettings((s) => ({ ...s, auto_sync_on_publish: v }))}
              />
              <SettingSwitch
                label="Include attachments"
                checked={settings.include_attachments ?? false}
                onChange={(v) => setSettings((s) => ({ ...s, include_attachments: v }))}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Max document depth</Label>
              <Input
                type="number"
                min={1}
                max={10}
                className="h-9"
                value={settings.max_depth ?? 3}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, max_depth: Number(e.target.value) || 1 }))
                }
              />
              <p className="text-[11px] text-muted-foreground">
                Limits how deep nested folder structures are indexed.
              </p>
            </div>

            <div className="space-y-3">
              <Label className="text-sm">Excluded keywords</Label>
              <div className="flex flex-wrap gap-2">
                {(settings.excluded_keywords || []).map((tag) => (
                  <Badge key={tag} variant="secondary" className="pl-2 pr-1 py-0.5 gap-1 text-[11px]">
                    {tag}
                    <button
                      onClick={() =>
                        setSettings((s) => ({
                          ...s,
                          excluded_keywords: s.excluded_keywords.filter((k) => k !== tag),
                        }))
                      }
                      className="hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Input
                placeholder="Type and press Enter..."
                className="h-9"
                value={keywordDraft}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || !keywordDraft.trim()) return;
                  e.preventDefault();
                  const tag = keywordDraft.trim();
                  setSettings((s) => ({
                    ...s,
                    excluded_keywords: [...new Set([...(s.excluded_keywords || []), tag])],
                  }));
                  setKeywordDraft('');
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sync history */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Sync History</h2>
        <Card className="overflow-hidden">
          {data.sync_logs.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Nothing synced yet.
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead className="w-[250px]">Space</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Processed</TableHead>
                  <TableHead className="text-right">When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.sync_logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-medium">{log.space_name}</TableCell>
                    <TableCell className="text-muted-foreground">{log.action}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {log.status === 'Success' ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                        )}
                        <span className="text-xs font-medium">{log.status}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.processed} docs · {log.chunks} chunks
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {formatWhen(log.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, dot }) => (
  <Card className="bg-card/50">
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase">{label}</p>
        <h3 className="text-2xl font-bold">{value}</h3>
      </div>
      {dot && <div className={cn('h-2 w-2 rounded-full', dot)} />}
    </CardContent>
  </Card>
);

const SettingSwitch = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between">
    <Label className="text-sm cursor-pointer">{label}</Label>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

const SyncBadge = ({ status }) => {
  const styles = {
    Synced: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    'Never synced': 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
    'Not indexed': 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  };
  const dots = {
    Synced: 'bg-emerald-500',
    'Never synced': 'bg-slate-500',
    'Not indexed': 'bg-slate-400',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
        styles[status] || styles['Not indexed']
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', dots[status] || dots['Not indexed'])} />
      {status}
    </span>
  );
};

export default KnowledgeSourcesPage;
